import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Crown, MicOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArenaParticipant } from "@/hooks/useLiveArenas";

interface ExpandedVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: ArenaParticipant;
  isCurrentUser: boolean;
  stream: MediaStream | null;
  isAdmin: boolean;
}

const ExpandedVideoModal: React.FC<ExpandedVideoModalProps> = ({
  isOpen,
  onClose,
  participant,
  isCurrentUser,
  stream,
  isAdmin,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play()
        .then(() => setVideoPlaying(true))
        .catch(err => console.error("[ExpandedVideo] Play failed:", err));
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  const isVideoActive = stream?.getVideoTracks()[0]?.enabled && 
                        stream?.getVideoTracks()[0]?.readyState === "live";

  const showVideo = stream && videoPlaying && isVideoActive;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-black">
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
        >
          <X className="w-5 h-5" />
        </Button>

        {/* Video / Avatar */}
        <div className="aspect-video relative bg-black">
          {showVideo ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted={isCurrentUser}
              className="w-full h-full object-cover"
              style={{ transform: isCurrentUser ? "scaleX(-1)" : undefined }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <Avatar className="w-24 h-24">
                <AvatarImage src={participant.profile?.photo_url} />
                <AvatarFallback className="text-3xl">
                  {(participant.profile?.name || "U").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          {/* Overlays */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex items-center gap-2">
              {isAdmin && (
                <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                  <Crown className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="text-white font-semibold text-lg">
                {isCurrentUser ? "You" : participant.profile?.name || "User"}
              </span>
              {participant.is_muted && (
                <div className="w-6 h-6 rounded-full bg-red-500/80 flex items-center justify-center">
                  <MicOff className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpandedVideoModal;
